import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const weekStartSunday = (ymd) => {
  const d = new Date(ymd + "T00:00:00");
  const off = d.getDay() % 7;
  d.setDate(d.getDate() - off);
  return d.toISOString().slice(0, 10);
};

/**
 * Compact card for the WTD Plan $ and LY Sales $ weekly inputs. These are
 * single-entry values for the whole store week (Sun→Sat) — the manager fills
 * them on Sunday and they carry through every EOD email for the week.
 * Separated from the Eyes-on-the-Biz Preview so it can sit above or beside
 * the Checkpoints tile on the dashboard.
 */
export default function WeeklyFiguresCard({ date, user, refreshKey = 0, onAfterSave, embedded = false }) {
  const [planInput, setPlanInput] = useState("");
  const [lyInput, setLyInput] = useState("");
  // Weekly Fill Rate input — surfaces in the printed Zone Sheet's KPI
  // box. ADS / UPT used to live here too but were pivoted to daily
  // inputs 2026-06-13 (see DailyKpiEditor inside EyesOnBizPreview).
  const [fillInput, setFillInput] = useState("");
  // 7-day arrays for Sunday-typed Flash Sales report entries. Manager
  // pulls Sun→Sat plan + LY off Five Below's Flash Sales report and
  // types them here so the Zone Sheet's Eyes-on-the-Biz "+/- Plan" and
  // "+/- LY" line up EXACTLY with Flash (elapsed-days-only totals).
  // User ask 2026-02-26 after showing the Flash mismatch.
  const [dailyPlan, setDailyPlan] = useState(["", "", "", "", "", "", ""]);
  const [dailyLy, setDailyLy] = useState(["", "", "", "", "", "", ""]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState({ plan: null, ly: null, fill_rate: null, daily_plan: null, daily_ly: null });
  // Admin override — admins can edit weekly figures even after they're
  // locked for the week. Toggled on via the small "Unlock" link that
  // only appears when the actor is an admin AND the values are saved.
  const [adminOverride, setAdminOverride] = useState(false);

  const weekStart = weekStartSunday(date);
  const isAdmin = !!user?.is_admin;

  // Weekly Figures are a Sunday-setup item — the manager enters them
  // once on Sunday and they carry through Sun→Sat. The previous build
  // kept the editor visible all week which made it look like a daily
  // input. From 2026-06-12 forward we hide the editor M-Sat and render
  // a compact read-only summary instead. Admins keep a one-tap unlock
  // so mid-week corrections are still possible without rolling back.
  const dayOfWeek = (() => {
    try { return new Date(date + "T00:00:00").getDay(); } catch { return 0; }
  })();
  const isSunday = dayOfWeek === 0;
  const showEditor = isSunday || adminOverride;

  // Lock state — once any weekly figure (Plan / LY / Fill Rate) has
  // been saved for the week, the inputs are display-only until next
  // Sunday. Admins can override via the unlock link.
  // User ask 2026-02-15: "weekly figures (locked after typed in)".
  const locked = (saved.plan !== null || saved.ly !== null || saved.fill_rate !== null) && !adminOverride;

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/biz/weekly-ly/${weekStart}`);
      setPlanInput(r.data?.plan_sales ?? "");
      setLyInput(r.data?.ly_sales ?? "");
      setFillInput(r.data?.fill_rate ?? "");
      const dpArr = Array.isArray(r.data?.daily_plan) ? r.data.daily_plan : null;
      const dlArr = Array.isArray(r.data?.daily_ly) ? r.data.daily_ly : null;
      setDailyPlan(dpArr ? dpArr.map((v) => v == null ? "" : String(v)) : ["", "", "", "", "", "", ""]);
      setDailyLy(dlArr ? dlArr.map((v) => v == null ? "" : String(v)) : ["", "", "", "", "", "", ""]);
      setSaved({
        plan: r.data?.plan_sales ?? null,
        ly: r.data?.ly_sales ?? null,
        fill_rate: r.data?.fill_rate ?? null,
        daily_plan: dpArr,
        daily_ly: dlArr,
      });
    } catch { /* non-critical */ }
  }, [weekStart]);

  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  const save = async () => {
    if (planInput === "" && lyInput === "" && fillInput === "") return;
    setSaving(true);
    try {
      await axios.put(`${API}/biz/weekly-ly`, {
        week_start: weekStart,
        plan_sales: planInput === "" ? null : Number(planInput),
        ly_sales: lyInput === "" ? null : Number(lyInput),
        fill_rate: fillInput === "" ? null : Number(fillInput),
        daily_plan: dailyPlan.map((v) => v === "" ? null : Number(v)),
        daily_ly: dailyLy.map((v) => v === "" ? null : Number(v)),
        actor_id: user?.crew_id,
      });
      toast.success("Weekly figures saved");
      await refresh();
      // Re-lock after every save so the admin "Unlock" override is
      // single-use per fix. Matches user spec "locked after typed in"
      // — if admin needs to fix something else, they click Admin Unlock
      // again.
      setAdminOverride(false);
      onAfterSave && onAfterSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  const dirty = (planInput !== "" ? Number(planInput) : null) !== saved.plan
             || (lyInput !== "" ? Number(lyInput) : null) !== saved.ly
             || (fillInput !== "" ? Number(fillInput) : null) !== saved.fill_rate;

  return (
    <div className={embedded ? "" : "dashboard-card"} data-testid="weekly-figures-card">
      {!embedded && (
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#FEF3C7" }}>
            <Calendar weight="bold" className="w-4 h-4 text-amber-700" />
          </div>
          <div>
            <h3 className="text-base font-bold tracking-tight" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
              Weekly figures <span className="text-[11px] font-semibold text-slate-400">· week of {weekStart}</span>
            </h3>
            <p className="text-[11px] text-slate-500">Enter on Sunday. Both carry through the full Sun → Sat week.</p>
          </div>
        </div>
      )}
      {/* M-Sat compact read-only view — values still carry through
          the EOD math, we just don't surface the editor when there's
          nothing for managers to do. Admin tap on "Admin unlock"
          reveals the editor for mid-week corrections. */}
      {!showEditor ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="weekly-figures-readonly">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Week of {weekStart}</div>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5 text-[12px] font-bold text-slate-800">
                <span data-testid="weekly-figures-plan-display">
                  WTD Plan: {saved.plan != null ? `$${Number(saved.plan).toLocaleString()}` : <em className="text-slate-400 font-normal">not set</em>}
                </span>
                <span data-testid="weekly-figures-ly-display">
                  LY Sales: {saved.ly != null ? `$${Number(saved.ly).toLocaleString()}` : <em className="text-slate-400 font-normal">not set</em>}
                </span>
                <span data-testid="weekly-figures-fill-rate-display">
                  Fill Rate: {saved.fill_rate != null ? `${Number(saved.fill_rate).toLocaleString()}%` : <em className="text-slate-400 font-normal">not set</em>}
                </span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">Set on Sunday · re-opens next Sunday</div>
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setAdminOverride(true)}
                className="text-[10px] font-bold text-slate-700 underline whitespace-nowrap"
                data-testid="weekly-admin-unlock-midweek"
              >Admin unlock</button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Lock banner — only when something has been saved this week.
              Renders a tiny "Unlock" link for admins so they can correct
              a mistyped figure without waiting until next Sunday. */}
          {locked && (
            <div className="flex items-center justify-between mb-2 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-[11px]">
              <span data-testid="weekly-locked-banner">
                Locked for week of {weekStart} · unlocks Sunday
              </span>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setAdminOverride(true)}
                  className="text-[10px] font-bold text-amber-900 underline"
                  data-testid="weekly-admin-unlock"
                >Admin unlock</button>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">WTD Plan $</label>
              <Input type="number" step="0.01" value={planInput} onChange={(e) => setPlanInput(e.target.value)} placeholder="68000" data-testid="weekly-plan-input" disabled={locked} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">LY Sales $</label>
              <Input type="number" step="0.01" value={lyInput} onChange={(e) => setLyInput(e.target.value)} placeholder="58000" data-testid="weekly-ly-input" disabled={locked} />
            </div>
          </div>
          {/* Weekly Fill Rate KPI — feeds the printed Zone Sheet KPI
              box. ADS/UPT were moved out 2026-06-13 to the daily Eyes
              on the Biz card (see `DailyKpiEditor`) since managers
              enter them in the AM, not weekly. */}
          <div className="grid grid-cols-1 gap-2 mt-2" data-testid="weekly-kpi-inputs">
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Fill Rate %</label>
              <Input type="number" step="0.01" value={fillInput} onChange={(e) => setFillInput(e.target.value)} placeholder="96" data-testid="weekly-fill-rate-input" disabled={locked} />
            </div>
          </div>
          {/* Daily Flash Sales entries (2026-02-26) — Sun→Sat per-day
              plan + LY. Copy straight from Five Below's Flash Sales
              report on Sunday. WTD math then matches Flash exactly
              instead of prorating. Empty cells fall back to the
              legacy proration behavior. */}
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/40 p-2" data-testid="weekly-daily-grid">
            <div className="text-[10px] uppercase tracking-wider font-black text-amber-800 mb-1.5">
              Flash Sales daily values (Sun→Sat)
            </div>
            <div className="grid grid-cols-7 gap-1">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d, i) => (
                <div key={d} className="min-w-0">
                  <div className="text-[9px] font-black uppercase text-slate-500 text-center">{d}</div>
                  <Input
                    type="number"
                    step="0.01"
                    value={dailyPlan[i]}
                    onChange={(e) => {
                      const next = [...dailyPlan];
                      next[i] = e.target.value;
                      setDailyPlan(next);
                    }}
                    placeholder="Plan"
                    disabled={locked}
                    className="text-xs px-1"
                    data-testid={`weekly-daily-plan-${i}`}
                  />
                  <Input
                    type="number"
                    step="0.01"
                    value={dailyLy[i]}
                    onChange={(e) => {
                      const next = [...dailyLy];
                      next[i] = e.target.value;
                      setDailyLy(next);
                    }}
                    placeholder="LY"
                    disabled={locked}
                    className="text-xs px-1 mt-1"
                    data-testid={`weekly-daily-ly-${i}`}
                  />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5">
              Top row = Plan $ · Bottom row = LY Sales $. Leave blank to prorate from the weekly totals above.
            </p>
          </div>
          {!locked && (
            <Button onClick={save} disabled={saving || !dirty} className="mt-2 w-full fb-btn-primary" data-testid="weekly-save">
              {saving ? "Saving…" : dirty ? "Save weekly figures" : "Saved"}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
